import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDentistViewImages } from '../../../Services/APIs/report';
import { simmetryImageUpload, submitSimmetryScan } from '../../../Services/APIs/scan';
import { getCroppedImg } from '../../../Utils/cropImageHelper';
import { encrypt } from '../../../Utils/hmac';
import { generateRandomAlphaNumeric } from '../../../Utils/alphaNumeric';
import { logEvent } from '../../../Utils/logEvent';
import { toImageObjectUrl, revokeImageUrl } from '../../../Utils/imageData';
import { getClinicDetails } from '../../../Utils/clinicAuth';
import ConfirmDialog from '../../../Components/UI/ConfirmDialog';
import ModalShell from './ModalShell';
import UploadStep from './UploadStep';
import ProcessingStep from './ProcessingStep';
import ResultStep from './ResultStep';
import SaveStep from './SaveStep';
import DoneStep from './DoneStep';
import SimulationError from './errors';
import { ERROR_KIND } from './constants';

const TITLES = {
    upload: 'Upload Photo',
    processing: 'Running Simulation',
    error: 'Simulation Failed',
    result: 'Simulation Complete',
    save: 'Patient Details',
    done: 'Ready to Send',
};

const generateUid = () =>
    `${generateRandomAlphaNumeric(2)}${Date.now().toString()}${generateRandomAlphaNumeric(1)}`;

const stripExtension = (name) => (typeof name === 'string' ? name.replace(/\.[^./]+$/, '') : name);

const log = (ctx, event, error) =>
    logEvent({ ...ctx, event: `dashboard simulation: ${event}`, error });

const cropPhoto = async (dataUrl, ctx) => {
    try {
        const blob = await getCroppedImg(dataUrl);
        return new File([blob], 'report_image.jpeg', { type: 'image/jpeg' });
    } catch (err) {
        log(ctx, 'image crop failed', err);
        throw err;
    }
};

const uploadImage = async (formData, ctx) => {
    try {
        return await simmetryImageUpload({ formData });
    } catch (err) {
        const status = err?.response?.status;
        log(ctx, status === 429 ? 'image upload busy (429)' : 'image upload failed', err);
        throw err;
    }
};

const scanImage = async (info, ctx) => {
    try {
        return await submitSimmetryScan(info);
    } catch (err) {
        const status = err?.response?.status;
        log(ctx, status === 429 ? 'scan busy (429)' : 'scan failed', err);
        throw err;
    }
};

const fetchResultImages = async (uid, imageName, ctx) => {
    try {
        return await getDentistViewImages(uid, stripExtension(imageName));
    } catch (err) {
        const status = err?.response?.status;
        log(ctx, status === 404 ? 'images not found (404)' : 'images fetch failed', err);
        throw err;
    }
};

const NewSimulationModal = ({ onClose, onSaved }) => {
    const navigate = useNavigate();

    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [photo, setPhoto] = useState(null);
    const [saveIntent, setSaveIntent] = useState(null);
    const [savedName, setSavedName] = useState(null);
    const [savedPatientId, setSavedPatientId] = useState(null);
    const [patientName, setPatientName] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const runIdRef = useRef(0);
    const objectUrlsRef = useRef([]);

    const trackUrl = (url) => {
        if (url) objectUrlsRef.current.push(url);
        return url;
    };

    const revokeTrackedUrls = () => {
        objectUrlsRef.current.forEach(revokeImageUrl);
        objectUrlsRef.current = [];
    };

    useEffect(() => revokeTrackedUrls, []);

    const close = () => {
        runIdRef.current += 1;
        onClose();
    };

    const run = async (dataUrl) => {
        const runId = runIdRef.current + 1;
        runIdRef.current = runId;
        const isStale = () => runIdRef.current !== runId;

        const fail = (kind) => {
            setRunning(false);
            setError({ kind });
        };

        setError(null);
        setResult(null);
        revokeTrackedUrls();
        setRunning(true);

        const company = getClinicDetails()?.company;
        if (!company) {
            fail(ERROR_KIND.NO_CLINIC);
            return;
        }

        const uid = generateUid();
        const ctx = { app: company, uid };

        try {
            const file = await cropPhoto(dataUrl, ctx);

            const formData = new FormData();
            formData.append('report_image', file);
            formData.append('_n', encrypt({ uid, folderName: uid, company }));

            const uploadResponse = await uploadImage(formData, ctx);
            if (isStale()) return;

            if (uploadResponse.data?.data?.is_reshoot) {
                log(ctx, 'image upload reshoot');
                fail(ERROR_KIND.RESHOOT);
                return;
            }

            const imageName = uploadResponse.data?.data?.imageName;
            if (!imageName) {
                log(ctx, 'upload returned no image name');
                fail(ERROR_KIND.NO_RESULT);
                return;
            }

            const scanResponse = await scanImage({ uid, folderName: uid, company, imageName }, ctx);
            if (isStale()) return;

            const scanData = scanResponse?.data?.data;
            if (scanData?.is_server_busy) {
                log(ctx, 'scan busy (flag)');
                fail(ERROR_KIND.BUSY);
                return;
            }
            if (scanData?.is_reshoot) {
                log(ctx, 'scan reshoot');
                fail(ERROR_KIND.RESHOOT);
                return;
            }

            const imagesResponse = await fetchResultImages(uid, imageName, ctx);
            if (isStale()) return;

            const { cropData, correctionData } = imagesResponse.data?.data || {};

            const afterImage = trackUrl(toImageObjectUrl(correctionData));
            if (!afterImage) {
                log(ctx, 'combo image data missing');
                fail(ERROR_KIND.NO_RESULT);
                return;
            }

            const beforeImage = trackUrl(toImageObjectUrl(cropData));
            if (!beforeImage) {
                log(ctx, 'crop image data missing');
            }

            log(ctx, 'succeeded');
            setResult({ uid, imageName, beforeImage: beforeImage || dataUrl, afterImage });
            setRunning(false);
        } catch (err) {
            if (isStale()) return;

            const status = err?.response?.status;

            if (status === 401 || err?.code === 'INVALID_TOKEN') {
                setRunning(false);
                return;
            }

            fail(
                status === 429 ? ERROR_KIND.BUSY
                    : status === 404 ? ERROR_KIND.NOT_FOUND
                        : ERROR_KIND.FAILED
            );
        }
    };

    const startRun = (dataUrl) => {
        setPhoto(dataUrl);
        run(dataUrl);
    };

    const chooseAnother = () => {
        runIdRef.current += 1;
        revokeTrackedUrls();
        setRunning(false);
        setResult(null);
        setError(null);
        setPhoto(null);
    };

    const handleSaved = (patientId, name, email) => {
        setSavedName(name);
        setSavedPatientId(patientId);
        setPatientEmail(email);
        if (onSaved) onSaved();
    };

    const backToResult = ({ name, email }) => {
        setPatientName(name);
        setPatientEmail(email);
        setSaveIntent(null);
    };

    const goToMessages = (chatToken) => {
        close();
        navigate(chatToken ? `/dashboard/messages/${chatToken}` : '/dashboard/messages');
    };

    const view =
        running ? 'processing'
            : error ? 'error'
                : savedName ? 'done'
                    : !result ? 'upload'
                        : saveIntent ? 'save'
                            : 'result';

    const requestClose = () => (view === 'result' ? setConfirmDiscard(true) : close());

    return (
        <>
            <ModalShell title={TITLES[view]} onRequestClose={requestClose}>
                {view === 'upload' && <UploadStep onPhotoReady={startRun} />}

                {view === 'processing' && <ProcessingStep photo={photo} />}

                {view === 'error' && (
                    <SimulationError
                        error={error}
                        onRetry={() => run(photo)}
                        onChooseAnother={chooseAnother}
                    />
                )}

                {view === 'result' && (
                    <ResultStep
                        result={result}
                        onSaveAndSend={() => setSaveIntent('share')}
                        onSave={() => setSaveIntent('save')}
                        onDiscard={() => setConfirmDiscard(true)}
                    />
                )}

                {view === 'save' && (
                    <SaveStep
                        uid={result.uid}
                        exitType={saveIntent}
                        initialName={patientName}
                        initialEmail={patientEmail}
                        onSaved={handleSaved}
                        onBack={backToResult}
                    />
                )}

                {view === 'done' && (
                    <DoneStep
                        patientName={savedName}
                        patientId={savedPatientId}
                        patientEmail={patientEmail}
                        exitType={saveIntent}
                        onGoToMessages={goToMessages}
                        onClose={close}
                    />
                )}
            </ModalShell>

            {confirmDiscard && (
                <ConfirmDialog
                    variant="light"
                    title="Discard this simulation?"
                    message="The result isn't saved yet and can't be recovered once you close."
                    confirmLabel="Discard"
                    cancelLabel="Keep it"
                    onConfirm={close}
                    onCancel={() => setConfirmDiscard(false)}
                />
            )}
        </>
    );
};

export default NewSimulationModal;
